<template>
  <section class="communication-container">
    <!-- No site selected: friendly empty state -->
    <div v-if="!siteId" class="communication-empty-state">
      <svg class="communication-empty-icon" xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10 9 9 9 8 9"/>
      </svg>
      <p class="communication-empty-title">{{ $t('communication_select_site_title') || 'Choose a site' }}</p>
      <p class="communication-empty-desc">{{ $t('communication_select_site') || 'Select a company and site from the filters above to view and add notes for that location.' }}</p>
    </div>

    <template v-else>
      <!-- Where am I? -->
      <div v-if="siteContextLabel" class="communication-badge" role="status">{{ siteContextLabel }}</div>

      <!-- One-line page purpose (dismissible; customers + internal team) -->
      <div
        v-if="!communicationIntroDismissed"
        class="communication-page-intro"
        role="region"
        :aria-label="$t('communication_page_intro_aria') || 'About this page'"
      >
        <div class="communication-page-intro-inner">
          <svg class="communication-page-intro-icon" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <div class="communication-page-intro-copy">
            <p class="communication-page-intro-title">{{ $t('communication_page_intro_title') || 'Site notes' }}</p>
            <p class="communication-page-intro-body">{{ $t('communication_page_intro_body') }}</p>
          </div>
        </div>
        <button
          type="button"
          class="communication-page-intro-dismiss"
          :aria-label="$t('communication_page_intro_dismiss') || 'Hide this tip'"
          @click="dismissPageIntro"
        >
          {{ $t('communication_page_intro_dismiss') || 'Hide this tip' }}
        </button>
      </div>

      <!-- Notes list (add note opens in modal above this area) -->
      <section class="communication-list-section communication-list-section--full" aria-labelledby="communication-list-title">
        <div class="communication-list-toolbar">
          <h2 id="communication-list-title" class="communication-list-title">
            <svg class="communication-list-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/>
              <line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
            {{ $t('communication_recent_notes') || 'Notes' }}
            <span v-if="!loading && !fetchError && entries.length" class="communication-count">({{ searchQuery ? filteredEntries.length + ' / ' + entries.length : entries.length }})</span>
          </h2>
          <div class="communication-toolbar-actions">
            <button
              type="button"
              class="communication-add-note-btn"
              :aria-label="$t('communication_add_note_open_aria') || 'Open add note'"
              @click="openAddNoteModal"
            >
              <svg class="communication-add-note-btn-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              <span>{{ $t('communication_add_note') || 'Add a note' }}</span>
            </button>
            <div v-if="entries.length > 0" class="communication-filter-bar" role="group" :aria-label="$t('communication_filters_group_aria') || 'Filter and sort notes'">
              <div class="communication-filter-field">
                <span class="communication-filter-label">{{ $t('communication_sort_label') }}</span>
                <Select
                  :value="sortBy"
                  class="form-select communication-toolbar-select--sort"
                  :placeholder="$t('communication_sort_label')"
                  @input="onSortByInput"
                >
                  <Option value="date-desc" :label="$t('communication_sort_date_newest') || 'Newest first'" />
                  <Option value="date-asc" :label="$t('communication_sort_date_oldest') || 'Oldest first'" />
                  <Option value="author" :label="$t('communication_sort_author') || 'By author'" />
                  <Option value="type" :label="$t('communication_sort_type') || 'By type'" />
                  <Option value="pinned" :label="$t('communication_sort_pinned') || 'Pinned first'" />
                </Select>
              </div>
              <div class="communication-filter-field">
                <span class="communication-filter-label">{{ $t('communication_filter_by_type') }}</span>
                <Select
                  :value="typeFilter"
                  class="form-select communication-toolbar-select--type"
                  :placeholder="$t('communication_filter_by_type')"
                  @input="onTypeFilterInput"
                >
                  <Option value="" :label="$t('communication_filter_all_types') || 'All types'" />
                  <Option value="general" :label="$t('communication_type_general') || 'General'" />
                  <Option value="update" :label="$t('communication_type_update') || 'Update'" />
                  <Option value="issue" :label="$t('communication_type_issue') || 'Issue'" />
                  <Option value="follow_up" :label="$t('communication_type_follow_up') || 'Follow-up'" />
                  <Option value="meeting" :label="$t('communication_type_meeting') || 'Meeting note'" />
                </Select>
              </div>
              <div class="communication-filter-field communication-filter-field--search">
                <span class="communication-filter-label">{{ $t('communication_search_label_short') || 'Search' }}</span>
                <input
                  v-model="searchQuery"
                  type="search"
                  class="communication-search"
                  :placeholder="$t('communication_search_placeholder') || 'Search by author or text...'"
                  :aria-label="$t('communication_search_aria_label')"
                />
              </div>
            </div>
            <button type="button" class="communication-refresh" :disabled="loading" :aria-label="$t('communication_refresh')" @click="fetchEntries">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="communication-list-content">
          <Loading v-if="loading" />
          <div v-else-if="fetchError" class="communication-card communication-error-card">
            <p class="communication-error-text">{{ $t('communication_load_failed') || 'Could not load notes.' }}</p>
            <button type="button" class="communication-retry" @click="fetchEntries">{{ $t('communication_retry') || 'Try again' }}</button>
          </div>
          <div v-else-if="!filteredEntries.length" class="communication-card communication-empty-list">
            <svg v-if="!searchQuery && !effectiveTypeFilter" class="communication-empty-list-icon" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
            <p class="communication-empty-list-text">
              <template v-if="searchQuery || effectiveTypeFilter">
                {{ $t('communication_no_matches') }}
                <span v-if="entries.length > 0" class="communication-filter-hint">
                  {{ entries.length === 1 ? $t('communication_filter_hidden_one') : $t('communication_filter_hidden_many', { n: entries.length }) }}
                </span>
              </template>
              <template v-else>
                {{ $t('communication_no_entries') || 'No notes yet.' }}
              </template>
            </p>
            <button v-if="searchQuery || effectiveTypeFilter" type="button" class="communication-clear-filters" @click="clearFilters">{{ $t('communication_clear_filters') || 'Clear filters' }}</button>
          </div>
          <ul v-else class="communication-entries" role="list">
          <li v-for="entry in filteredEntries" :key="entry.id" :ref="'entry-' + entry.id" class="communication-entry-card" :class="{ 'communication-entry-pinned': isEntryPinned(entry) }">
            <div class="communication-entry-header">
              <div class="communication-entry-byline">
                <span v-if="isEntryPinned(entry)" class="communication-pinned-badge" :aria-label="$t('communication_pinned')">{{ $t('communication_pinned') || 'Pinned' }}</span>
                <span class="communication-entry-type-badge" :class="'communication-type--' + (entry.type || 'general')">{{ typeLabel(entry.type || 'general') }}</span>
                <span class="communication-entry-author">{{ entry.authorName || entry.author_name || '—' }}</span>
                <span v-if="entry.authorEmail || entry.author_email" class="communication-entry-email">{{ entry.authorEmail || entry.author_email }}</span>
              </div>
              <div class="communication-entry-meta">
                <time class="communication-entry-date" :datetime="entry.createdAt || entry.created_at" :title="formatDateFull(entry.createdAt || entry.created_at)">{{ formatDateRelative(entry.createdAt || entry.created_at) }}</time>
                <button
                  v-if="isAdmin"
                  type="button"
                  class="communication-pin"
                  :class="{ 'communication-pin--active': isEntryPinned(entry) }"
                  :disabled="pinningId === entry.id || (isEntryPinned(entry) ? false : pinnedCount >= 2)"
                  :aria-label="isEntryPinned(entry) ? ($t('communication_unpin') || 'Unpin') : ($t('communication_pin') || 'Pin')"
                  :title="isEntryPinned(entry) ? ($t('communication_unpin') || 'Unpin') : (pinnedCount >= 2 && !isEntryPinned(entry) ? ($t('communication_pin_limit') || 'Max 2 pinned') : ($t('communication_pin') || 'Pin'))"
                  @click="togglePin(entry)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 17v5"/>
                    <path d="M9 10V7a3 3 0 0 1 6 0v3"/>
                    <path d="M9 10h6"/>
                    <circle cx="12" cy="7" r="3"/>
                  </svg>
                </button>
                <button
                  type="button"
                  class="communication-copy"
                  :aria-label="$t('communication_copy_note_aria_label') || 'Copy note text'"
                  :title="$t('communication_copy_note_aria_label') || 'Copy note text'"
                  @click="copyNoteText(entry)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                </button>
                <button
                  v-if="isEntryAuthor(entry)"
                  type="button"
                  class="communication-edit"
                  :aria-label="$t('communication_edit') || 'Edit note'"
                  :title="$t('communication_edit') || 'Edit note'"
                  @click="startEdit(entry)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button
                  v-if="isEntryAuthor(entry)"
                  type="button"
                  class="communication-delete"
                  :aria-label="$t('communication_delete') || 'Delete note'"
                  :title="$t('communication_delete') || 'Delete note'"
                  @click="confirmDelete(entry)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>
              </div>
            </div>
            <div class="communication-entry-body">
              <div v-if="editingId === entry.id" class="communication-entry-edit">
                <textarea
                  v-model="editContent"
                  class="communication-edit-textarea"
                  rows="4"
                  :placeholder="$t('communication_content_placeholder') || 'Write your note here...'"
                />
                <div class="communication-edit-actions">
                  <div class="communication-edit-buttons">
                    <button type="button" class="communication-edit-cancel" @click="cancelEdit">{{ $t('communication_cancel') || 'Cancel' }}</button>
                    <button type="button" class="communication-edit-save" :disabled="!editContent.trim() || savingEdit" @click="saveEdit(entry)">
                      {{ savingEdit ? ($t('communication_saving') || 'Saving...') : ($t('communication_save') || 'Save') }}
                    </button>
                  </div>
                </div>
              </div>
              <div v-else>
                <div v-if="!isLongNote(entry.content)" class="communication-entry-content" v-html="renderMarkdown(entry.content)"></div>
                <div v-else>
                  <div v-if="!expandedNotes[entry.id]" class="communication-entry-content" v-html="renderMarkdown(truncateNote(entry.content))"></div>
                  <div v-else class="communication-entry-content" v-html="renderMarkdown(entry.content)"></div>
                  <button type="button" class="communication-expand-toggle" @click="toggleExpand(entry.id)">
                    {{ expandedNotes[entry.id] ? ($t('communication_show_less') || 'Show less') : ($t('communication_show_more') || 'Show more') }}
                  </button>
                </div>
                <span v-if="entry.updated_at || entry.updatedAt" class="communication-entry-edited">{{ $t('communication_edited') || 'Edited' }}</span>
              </div>
            </div>
          </li>
          </ul>
        </div>
      </section>
    </template>

    <!-- Add note modal (teleported to .main; sits above list visually) -->
    <Modal
      v-if="addNoteModalOpen"
      width="large"
      parent="shift"
      :showCancelBtn="false"
      :showConfirmBtn="false"
      :showCloseCorner="true"
      @close="closeAddNoteModal"
    >
      <template #header>
        <div class="communication-add-modal-header">
          <svg class="communication-add-modal-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          <span class="communication-add-modal-title">{{ $t('communication_add_note') || 'Add a note' }}</span>
        </div>
      </template>
      <div class="communication-modal-form">
        <p class="communication-modal-form-desc">{{ $t('communication_form_hint') || 'Share updates or notes with everyone who has access to this site. They’ll see new notes in real time.' }}</p>
        <label for="communication-note-modal-input" class="communication-label visually-hidden">{{ $t('communication_content_placeholder') || 'Your note...' }}</label>
        <textarea
          id="communication-note-modal-input"
          ref="noteModalTextarea"
          v-model="formContent"
          class="communication-textarea communication-textarea--modal"
          :placeholder="$t('communication_content_placeholder') || 'Write your note here...'"
          rows="6"
          aria-describedby="communication-modal-keyboard-hint"
          @keydown.ctrl.enter.exact.prevent="submitEntry"
          @keydown.meta.enter.exact.prevent="submitEntry"
        />
        <div class="communication-form-row">
          <label class="communication-type-label">{{ $t('communication_note_type') || 'Note type' }}</label>
          <div class="communication-type-selector">
            <button
              type="button"
              class="communication-type-chip"
              :class="{ 'communication-type-chip--active': formType === 'general' }"
              :aria-label="$t('communication_type_general') || 'General'"
              :title="$t('communication_type_general') || 'General'"
              @click="formType = 'general'"
            >
              <svg class="communication-type-chip-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :title="$t('communication_type_general') || 'General'">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{{ $t('communication_type_general') || 'General' }}</span>
            </button>
            <button
              type="button"
              class="communication-type-chip"
              :class="{ 'communication-type-chip--active': formType === 'update' }"
              :aria-label="$t('communication_type_update') || 'Update'"
              :title="$t('communication_type_update') || 'Update'"
              @click="formType = 'update'"
            >
              <svg class="communication-type-chip-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :title="$t('communication_type_update') || 'Update'">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              <span>{{ $t('communication_type_update') || 'Update' }}</span>
            </button>
            <button
              type="button"
              class="communication-type-chip"
              :class="{ 'communication-type-chip--active': formType === 'issue' }"
              :aria-label="$t('communication_type_issue') || 'Issue'"
              :title="$t('communication_type_issue') || 'Issue'"
              @click="formType = 'issue'"
            >
              <svg class="communication-type-chip-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :title="$t('communication_type_issue') || 'Issue'">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span>{{ $t('communication_type_issue') || 'Issue' }}</span>
            </button>
            <button
              type="button"
              class="communication-type-chip"
              :class="{ 'communication-type-chip--active': formType === 'follow_up' }"
              :aria-label="$t('communication_type_follow_up') || 'Follow-up'"
              :title="$t('communication_type_follow_up') || 'Follow-up'"
              @click="formType = 'follow_up'"
            >
              <svg class="communication-type-chip-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :title="$t('communication_type_follow_up') || 'Follow-up'">
                <polyline points="9 11 12 14 22 4"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
              <span>{{ $t('communication_type_follow_up') || 'Follow-up' }}</span>
            </button>
            <button
              type="button"
              class="communication-type-chip"
              :class="{ 'communication-type-chip--active': formType === 'meeting' }"
              :aria-label="$t('communication_type_meeting') || 'Meeting note'"
              :title="$t('communication_type_meeting') || 'Meeting note'"
              @click="formType = 'meeting'"
            >
              <svg class="communication-type-chip-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :title="$t('communication_type_meeting') || 'Meeting note'">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <span>{{ $t('communication_type_meeting') || 'Meeting note' }}</span>
            </button>
          </div>
        </div>
        <p id="communication-modal-keyboard-hint" class="communication-keyboard-hint communication-keyboard-hint--modal">{{ $t('communication_keyboard_hint') || 'Ctrl+Enter to post' }}</p>
      </div>
      <template #footer>
        <div class="communication-modal-footer-actions">
          <button type="button" class="communication-modal-btn-cancel" @click="closeAddNoteModal">
            {{ $t('communication_cancel') || 'Cancel' }}
          </button>
          <button
            type="button"
            class="communication-submit"
            :disabled="!formContent.trim() || submitting"
            :aria-busy="submitting"
            :aria-label="$t('communication_submit_aria_label')"
            @click="submitEntry"
          >
            {{ submitting ? ($t('communication_submitting') || 'Posting...') : ($t('communication_submit') || 'Post note') }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Delete Confirmation Modal -->
    <Modal
      v-if="deletingEntryId"
      width="small"
      parent="shift"
      :texts="{
        headerPrimary: 'communication_delete_confirm_title',
        footerCancel: 'cancel',
        footerConfirm: 'communication_delete',
      }"
      :showCloseCorner="true"
      :showCancelBtn="true"
      :showConfirmBtn="true"
      @close="closeDeleteModal"
      @submit="handleDeleteConfirm"
    >
      <template #header>
        <div class="communication-delete-modal-header">
          <svg class="communication-delete-modal-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
          <span class="communication-delete-modal-title">{{ $t('communication_delete_confirm_title') || 'Delete Note' }}</span>
        </div>
      </template>
      <div class="communication-delete-modal-content">
        <p class="communication-delete-modal-message">
          {{ $t('communication_delete_confirm') || 'Are you sure you want to delete this note? This action cannot be undone.' }}
        </p>
        <div v-if="deletingEntryId" class="communication-delete-modal-preview">
          <div class="communication-delete-modal-preview-label">{{ $t('communication_note_preview') || 'Note preview:' }}</div>
          <div class="communication-delete-modal-preview-content">
            {{ getEntryPreview(deletingEntryId) }}
          </div>
        </div>
      </div>
    </Modal>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import io from 'socket.io-client/dist/socket.io';
import Loading from '@/modules/common/components/Loading';
import Modal from '@/modules/common/components/Modal';
import Select from '@/modules/common/components/Select';
import Option from '@/modules/common/components/Option';
import { Toast } from '@/modules/common/Toast';
import { getEntries, addEntry, setPinned, updateEntry, deleteEntry } from '../services/communicationService';
import { renderCommunicationMarkdown } from './communication/communicationMarkdown';
import {
  SOCKET_URL,
  COMMUNICATION_FETCH_TIMEOUT_MS,
  COMMUNICATION_REQUEST_TIMEOUT,
  COMMUNICATION_PAGE_INTRO_DISMISSED_KEY,
} from './communication/communicationConstants';

export default {
  data() {
    return {
      entries: [],
      loading: false,
      submitting: false,
      fetchError: false,
      formContent: '',
      formType: 'general',
      searchQuery: '',
      typeFilter: '',
      pinningId: null,
      socket: null,
      joinedSiteId: null,
      sortBy: 'pinned',
      editingId: null,
      editContent: '',
      savingEdit: false,
      expandedNotes: {},
      NOTE_TRUNCATE_LENGTH: 300,
      deletingEntryId: null,
      addNoteModalOpen: false,
      communicationIntroDismissed: false,
      listFetchGeneration: 0,
      /** Same-site concurrent fetchEntries deduped to this promise so a slower request is not discarded by listFetchGeneration. */
      entriesListFetchPromise: null,
      entriesListFetchSiteId: null,
    };
  },
  computed: {
    ...mapGetters(['filterSelected', 'currentSiteInfo', 'loggedUser']),
    filterOptions() {
      return this.$store.state.filter?.filterOptions || null;
    },
    siteId() {
      const id = this.filterSelected?.site ?? null;
      return id ? Number(id) : null;
    },
    siteContextLabel() {
      const name = this.currentSiteInfo?.name;
      if (!name) return '';
      return `${this.$t('communication_viewing_site')}: ${name}`;
    },
    isAdmin() {
      return this.loggedUser && this.loggedUser.type === 'admin';
    },
    pinnedCount() {
      return this.entries.filter((e) => this.isEntryPinned(e)).length;
    },
    /**
     * MdSelect sometimes emits non-string values; using them in === filters hides every row while entries.length stays &gt; 0.
     */
    effectiveTypeFilter() {
      let tf = this.typeFilter;
      if (tf != null && typeof tf === 'object' && !Array.isArray(tf)) {
        if ('value' in tf) {
          tf = tf.value;
        } else {
          return '';
        }
      }
      if (tf == null || tf === '') {
        return '';
      }
      const s = String(tf).trim();
      return s === '[object Object]' ? '' : s;
    },
    communicationSortKey() {
      let s = this.sortBy;
      if (s != null && typeof s === 'object' && !Array.isArray(s)) {
        if ('value' in s) {
          s = s.value;
        } else {
          return 'pinned';
        }
      }
      if (s == null || s === '') {
        return 'pinned';
      }
      const str = String(s).trim();
      if (!str || str === '[object Object]') {
        return 'pinned';
      }
      return str;
    },
    filteredEntries() {
      try {
        const raw = Array.isArray(this.entries) ? this.entries : [];
        let list = raw;
        const tf = this.effectiveTypeFilter;
        if (tf) {
          list = list.filter((e) => (e.type || 'general') === tf);
        }
        const q = typeof this.searchQuery === 'string' ? this.searchQuery.trim().toLowerCase() : '';
        if (q) {
          list = list.filter((e) => {
            const author = (e.authorName || e.author_name || '').toLowerCase();
            const email = (e.authorEmail || e.author_email || '').toLowerCase();
            const content = (e.content || '').toLowerCase();
            return author.includes(q) || email.includes(q) || content.includes(q);
          });
        }
        const sorted = [...list];
        switch (this.communicationSortKey) {
          case 'date-desc':
            sorted.sort((a, b) => {
              const dateA = new Date(a.createdAt || a.created_at);
              const dateB = new Date(b.createdAt || b.created_at);
              return dateB - dateA;
            });
            break;
          case 'date-asc':
            sorted.sort((a, b) => {
              const dateA = new Date(a.createdAt || a.created_at);
              const dateB = new Date(b.createdAt || b.created_at);
              return dateA - dateB;
            });
            break;
          case 'author':
            sorted.sort((a, b) => {
              const nameA = (a.authorName || a.author_name || '').toLowerCase();
              const nameB = (b.authorName || b.author_name || '').toLowerCase();
              return nameA.localeCompare(nameB);
            });
            break;
          case 'type':
            sorted.sort((a, b) => {
              const typeA = (a.type || 'general').toLowerCase();
              const typeB = (b.type || 'general').toLowerCase();
              return typeA.localeCompare(typeB);
            });
            break;
          case 'pinned':
          default:
            sorted.sort((a, b) => {
              const pinnedA = this.isEntryPinned(a) ? 1 : 0;
              const pinnedB = this.isEntryPinned(b) ? 1 : 0;
              if (pinnedA !== pinnedB) return pinnedB - pinnedA;
              const dateA = new Date(a.createdAt || a.created_at);
              const dateB = new Date(b.createdAt || b.created_at);
              return dateB - dateA;
            });
            break;
        }
        return sorted;
      } catch (err) {
        console.error('[Communication] filteredEntries failed:', err);
        return Array.isArray(this.entries) ? [...this.entries] : [];
      }
    },
  },
  watch: {
    siteId: {
      immediate: true,
      handler(siteId, oldSiteId) {
        if (oldSiteId != null && siteId != null && Number(oldSiteId) !== Number(siteId)) {
          this.entries = [];
          this.addNoteModalOpen = false;
        }
        if (siteId) {
          this.fetchError = false;
          if (this.socket && this.joinedSiteId && Number(this.joinedSiteId) !== Number(siteId)) {
            this.socket.emit('communication-leave', { siteId: this.joinedSiteId });
            this.joinedSiteId = null;
          }
          this.fetchEntries();
          this.joinCommunicationRoom(siteId);
          this.$nextTick(() => {
            this.loadDraft();
          });
        } else {
          this.entries = [];
          this.fetchError = false;
          this.loading = false;
          this.addNoteModalOpen = false;
          this.leaveCommunicationRoom();
          this.clearDraft();
        }
      },
    },
    entries(entries, oldEntries) {
      // Fresh load: MdSelect / persisted state can leave typeFilter or search in a shape that hides every row.
      const hadNoList = !oldEntries || oldEntries.length === 0;
      if (hadNoList && entries.length > 0) {
        this.$nextTick(() => {
          if (this.loading) return;
          if (this.entries.length > 0 && this.filteredEntries.length === 0) {
            this.typeFilter = '';
            this.searchQuery = '';
            this.sortBy = 'pinned';
          }
        });
      }
    },
    formContent() {
      if (this.siteId) {
        this.saveDraft();
      }
    },
  },
  created() {
    try {
      this.communicationIntroDismissed =
        typeof localStorage !== 'undefined' &&
        localStorage.getItem(COMMUNICATION_PAGE_INTRO_DISMISSED_KEY) === '1';
    } catch {
      this.communicationIntroDismissed = false;
    }
  },
  mounted() {
    if (this.$route.name !== 'communication') return;
    if (this.siteId) {
      this.loadDraft();
    }
    // List load: siteId watcher (immediate) + router ensure filter before mount. Do not call fetchEntries here — a second
    // call bumps listFetchGeneration and used to interact badly with loading cleared only for the “current” gen.
  },
  beforeDestroy() {
    this.leaveCommunicationRoom();
  },
  methods: {
    dismissPageIntro() {
      this.communicationIntroDismissed = true;
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(COMMUNICATION_PAGE_INTRO_DISMISSED_KEY, '1');
        }
      } catch {
        // ignore
      }
    },
    openAddNoteModal() {
      this.addNoteModalOpen = true;
      this.$nextTick(() => {
        const el = this.$refs.noteModalTextarea;
        if (el && typeof el.focus === 'function') {
          el.focus();
        }
      });
    },
    closeAddNoteModal() {
      this.addNoteModalOpen = false;
    },
    joinCommunicationRoom(siteId) {
      const id = Number(siteId);
      if (!id) return;
      if (!this.socket) {
        this.socket = io(SOCKET_URL, { transports: ['websocket'], withCredentials: true });
        this.socket.on('site-communication:new-entry', (entry) => {
          if (!entry || !entry.id) return;
          const entrySiteId = entry.site_id != null ? entry.site_id : entry.siteId;
          if (Number(entrySiteId) !== Number(this.siteId)) {
            return;
          }
          const exists = this.entries.some((e) => e && e.id === entry.id);
          if (!exists) {
            this.entries = [this.normalizeEntryFromApi(entry), ...this.entries];
          }
        });
        this.socket.on('site-communication:updated-entry', (entry) => {
          if (!entry || !entry.id) return;
          const entrySiteId = entry.site_id != null ? entry.site_id : entry.siteId;
          if (Number(entrySiteId) !== Number(this.siteId)) return;
          const index = this.entries.findIndex((e) => e && e.id === entry.id);
          if (index >= 0) {
            this.$set(this.entries, index, this.normalizeEntryFromApi(entry, this.entries[index]));
          } else {
            const exists = this.entries.some((e) => e && e.id === entry.id);
            if (!exists) {
              const normalized = this.normalizeEntryFromApi(entry);
              if (normalized) {
                this.entries = [normalized, ...this.entries];
              }
            }
          }
        });
        this.socket.on('site-communication:deleted-entry', ({ entryId }) => {
          if (!entryId) return;
          this.entries = this.entries.filter((e) => e && e.id !== entryId);
          if (this.editingId === entryId) {
            this.cancelEdit();
          }
          if (this.deletingEntryId === entryId) {
            this.deletingEntryId = null;
          }
        });
      }
      this.socket.emit('communication-log', { siteId: id });
      this.joinedSiteId = id;
    },
    leaveCommunicationRoom() {
      if (this.socket && this.joinedSiteId) {
        try {
          this.socket.emit('communication-leave', { siteId: this.joinedSiteId });
        } catch (err) {
          // Socket might already be disconnected
        }
        this.joinedSiteId = null;
      }
      if (this.socket) {
        try {
          this.socket.removeAllListeners();
          this.socket.disconnect();
        } catch (err) {
          // Socket might already be disconnected
        }
        this.socket = null;
      }
    },
    async fetchEntries() {
      if (!this.siteId) {
        this.loading = false;
        return;
      }
      const siteIdForThisFetch = Number(this.siteId);

      if (
        this.entriesListFetchPromise &&
        this.entriesListFetchSiteId === siteIdForThisFetch
      ) {
        return this.entriesListFetchPromise;
      }

      this.listFetchGeneration += 1;
      const gen = this.listFetchGeneration;

      this.loading = true;
      this.fetchError = false;

      const listPromise = (async () => {
        try {
          let fetchTimeoutId;
          const list = await new Promise((resolve, reject) => {
            fetchTimeoutId = setTimeout(() => {
              reject(new Error(COMMUNICATION_REQUEST_TIMEOUT));
            }, COMMUNICATION_FETCH_TIMEOUT_MS);
            getEntries(siteIdForThisFetch)
              .then((v) => {
                clearTimeout(fetchTimeoutId);
                resolve(v);
              })
              .catch((e) => {
                clearTimeout(fetchTimeoutId);
                reject(e);
              });
          });
          if (gen !== this.listFetchGeneration) {
            return;
          }
          if (!Array.isArray(list)) {
            this.entries = [];
          } else {
            const normalized = list
              .map((row) => this.normalizeEntryFromApi(row))
              .filter((e) => e != null && e.id != null && !Number.isNaN(Number(e.id)));
            this.entries = normalized;
          }
        } catch (err) {
          if (gen !== this.listFetchGeneration) {
            return;
          }
          console.error('[Communication] Failed to fetch entries:', err?.message || err, err?.response?.data || '');
          this.entries = [];
          this.fetchError = true;
          const errorMsg =
            err.message === COMMUNICATION_REQUEST_TIMEOUT
              ? this.$t('communication_request_timeout')
              : this.$t('communication_load_failed');
          new Toast({
            message: errorMsg,
            type: 'error',
            duration: 5000,
          });
        } finally {
          if (gen === this.listFetchGeneration) {
            this.loading = false;
          }
          if (this.entriesListFetchPromise === listPromise) {
            this.entriesListFetchPromise = null;
            this.entriesListFetchSiteId = null;
          }
        }
      })();

      this.entriesListFetchSiteId = siteIdForThisFetch;
      this.entriesListFetchPromise = listPromise;
      return listPromise;
    },
    async submitEntry() {
      const content = this.formContent.trim();
      if (!content || !this.siteId || this.submitting) return;
      this.submitting = true;
      try {
        const newEntry = await addEntry(this.siteId, { content, type: this.formType });
        // Add immediately to the list for instant feedback
        // The backend returns fullEntry with author info, so we can use it directly
        if (newEntry && newEntry.id) {
          const exists = this.entries.some((e) => e && e.id === newEntry.id);
          if (!exists) {
            this.entries = [this.normalizeEntryFromApi(newEntry), ...this.entries];
          }
        }
        this.formContent = '';
        this.clearDraft();
        this.closeAddNoteModal();
        new Toast({
          message: this.$t('communication_note_posted') || 'Note posted.',
          type: 'success',
          duration: 2000,
        });
      } catch (err) {
        console.error('[Communication] Failed to add entry:', err);
        const errorMessage = err.response?.data || err.message || this.$t('communication_post_failed') || 'Could not post note. Please try again.';
        new Toast({
          message: typeof errorMessage === 'string' ? errorMessage : (this.$t('communication_post_failed') || 'Could not post note. Please try again.'),
          type: 'error',
          duration: 5000,
        });
      } finally {
        this.submitting = false;
      }
    },
    formatDate(isoString) {
      if (!isoString) return '—';
      const d = new Date(isoString);
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale.replace(/_/g, '-') : undefined;
      try {
        if (typeof d.toLocaleString === 'function' && locale) {
          return d.toLocaleString(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        }
      } catch {
        void 0;
      }
      return d.toLocaleString();
    },
    formatDateFull(isoString) {
      if (!isoString || typeof isoString !== 'string') return '';
      return this.formatDate(isoString);
    },
    formatDateRelative(isoString) {
      if (!isoString || typeof isoString !== 'string') return '—';
      const d = new Date(isoString);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffMins < 1) return this.$t('communication_just_now') || 'Just now';
      if (diffMins < 60) return this.$t('communication_minutes_ago', { n: diffMins }) || `${diffMins} min ago`;
      if (diffHours < 24) return this.$t('communication_hours_ago', { n: diffHours }) || `${diffHours} hr ago`;
      if (diffDays < 7) return this.$t('communication_days_ago', { n: diffDays }) || `${diffDays} days ago`;
      return this.formatDate(isoString);
    },
    typeLabel(type) {
      const key = `communication_type_${type}`;
      return this.$t(key) || type;
    },
    onTypeFilterInput(value) {
      if (value == null || value === '') {
        this.typeFilter = '';
        return;
      }
      if (typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
        this.typeFilter = value.value == null ? '' : String(value.value);
        return;
      }
      this.typeFilter = String(value);
    },
    onSortByInput(value) {
      if (value == null || value === '') {
        this.sortBy = 'pinned';
        return;
      }
      if (typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
        this.sortBy = value.value == null ? 'pinned' : String(value.value);
        return;
      }
      this.sortBy = String(value);
    },
    /** Merge API/snake_case fields with camelCase used in the UI. Optional previous row preserves author when API omits joins. */
    normalizeEntryFromApi(entry, previous = null) {
      if (entry == null || typeof entry !== 'object' || Array.isArray(entry)) {
        return null;
      }
      return {
        ...entry,
        id: entry.id != null ? Number(entry.id) : entry.id,
        authorName: entry.author_name || entry.authorName || (previous && previous.authorName),
        authorEmail: entry.author_email || entry.authorEmail || (previous && previous.authorEmail),
        createdAt: entry.created_at || entry.createdAt || (previous && previous.createdAt),
      };
    },
    isEntryPinned(entry) {
      if (!entry) return false;
      return Number(entry.is_pinned) === 1 || entry.isPinned === true || entry.is_pinned === true;
    },
    isEntryAuthor(entry) {
      if (!this.loggedUser || !entry || !this.loggedUser.id) return false;
      const entryUserId = entry.user_id || entry.userId;
      if (!entryUserId) return false;
      return Number(entryUserId) === Number(this.loggedUser.id);
    },
    startEdit(entry) {
      this.editingId = entry.id;
      this.editContent = entry.content || '';
      this.$nextTick(() => {
        const textarea = this.$el.querySelector('.communication-edit-textarea');
        if (textarea) textarea.focus();
      });
    },
    cancelEdit() {
      this.editingId = null;
      this.editContent = '';
    },
    async saveEdit(entry) {
      if (!entry || !entry.id || !this.editContent.trim() || !this.siteId || this.savingEdit) return;
      this.savingEdit = true;
      try {
        const updated = await updateEntry(this.siteId, entry.id, { content: this.editContent.trim(), type: entry.type || 'general' });
        this.cancelEdit();
        // Don't need to fetchEntries - socket will update the list
        // But update locally immediately for instant feedback
        const index = this.entries.findIndex((e) => e && e.id === entry.id);
        if (index >= 0 && updated) {
          this.$set(this.entries, index, this.normalizeEntryFromApi(updated, this.entries[index]));
        }
        new Toast({
          message: this.$t('communication_note_updated') || 'Note updated.',
          type: 'success',
          duration: 2000,
        });
      } catch (err) {
        console.error('[Communication] Failed to update entry:', err);
        await this.fetchEntries();
        new Toast({
          message: this.$t('communication_update_failed') || 'Failed to update note. Please try again.',
          type: 'error',
          duration: 4000,
        });
      } finally {
        this.savingEdit = false;
      }
    },
    confirmDelete(entry) {
      if (!entry || !entry.id) return;
      this.deletingEntryId = entry.id;
    },
    closeDeleteModal() {
      this.deletingEntryId = null;
    },
    async handleDeleteConfirm() {
      if (!this.deletingEntryId) return;
      const entry = this.entries.find((e) => e.id === this.deletingEntryId);
      if (!entry) {
        new Toast({
          message: this.$t('communication_note_not_found') || 'Note not found or you don\'t have access.',
          type: 'error',
          duration: 2000,
        });
        this.closeDeleteModal();
        return;
      }
      await this.deleteEntry(entry);
      // On success deleteEntry clears deletingEntryId (modal closes). On failure modal stays open for retry.
    },
    async deleteEntry(entry) {
      if (!this.siteId || !entry || !entry.id) return;
      const entryId = entry.id;
      try {
        await deleteEntry(this.siteId, entryId);
        this.entries = this.entries.filter((e) => e.id !== entryId);
        if (this.editingId === entryId) {
          this.cancelEdit();
        }
        if (this.deletingEntryId === entryId) {
          this.deletingEntryId = null;
        }
        new Toast({
          message: this.$t('communication_note_deleted') || 'Note deleted.',
          type: 'success',
          duration: 2000,
        });
      } catch (err) {
        console.error('[Communication] Failed to delete entry:', err);
        await this.fetchEntries();
        new Toast({
          message: this.$t('communication_delete_failed') || 'Failed to delete note. Please try again.',
          type: 'error',
          duration: 4000,
        });
      }
    },
    getEntryPreview(entryId) {
      if (!entryId) return '';
      const entry = this.entries.find((e) => e && e.id === entryId);
      if (!entry) return '';
      const content = entry.content || '';
      if (!content) return '';
      return content.length > 150 ? content.substring(0, 150) + '...' : content;
    },
    isLongNote(content) {
      if (!content || typeof content !== 'string') return false;
      return content.length > this.NOTE_TRUNCATE_LENGTH;
    },
    truncateNote(content) {
      if (!content || typeof content !== 'string') return '';
      if (content.length <= this.NOTE_TRUNCATE_LENGTH) return content;
      return content.substring(0, this.NOTE_TRUNCATE_LENGTH) + '...';
    },
    toggleExpand(entryId) {
      if (!entryId) return;
      this.$set(this.expandedNotes, entryId, !this.expandedNotes[entryId]);
    },
    renderMarkdown(text) {
      return renderCommunicationMarkdown(text);
    },
    copyNoteText(entry) {
      if (!entry) return;
      const text = String(entry.content ?? '').trim();
      if (!text) {
        new Toast({
          message: this.$t('communication_copy_empty') || 'There is no note text to copy.',
          type: 'info',
          duration: 2200,
        });
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          new Toast({
            message: this.$t('communication_copied') || 'Copied to clipboard.',
            type: 'success',
            duration: 2500,
          });
        }).catch(() => this.fallbackCopy(text));
      } else {
        this.fallbackCopy(text);
      }
    },
    async togglePin(entry) {
      if (!entry || !entry.id || !this.siteId || this.pinningId !== null) return;
      const pinned = !this.isEntryPinned(entry);
      this.pinningId = entry.id;
      try {
        const { entry: updated, autoUnpinnedEntry } = await setPinned(this.siteId, entry.id, pinned);
        if (autoUnpinnedEntry && autoUnpinnedEntry.id) {
          const unIdx = this.entries.findIndex((e) => e && e.id === autoUnpinnedEntry.id);
          if (unIdx >= 0) {
            this.$set(this.entries, unIdx, this.normalizeEntryFromApi(autoUnpinnedEntry, this.entries[unIdx]));
          }
        }
        const index = this.entries.findIndex((e) => e && e.id === entry.id);
        if (index >= 0 && updated) {
          this.$set(this.entries, index, this.normalizeEntryFromApi(updated, this.entries[index]));
        }
        new Toast({
          message: pinned ? (this.$t('communication_pinned_toast') || 'Note pinned.') : (this.$t('communication_unpinned_toast') || 'Note unpinned.'),
          type: 'success',
          duration: 2000,
        });
      } catch (err) {
        console.error('[Communication] Failed to set pinned:', err);
        new Toast({
          message: this.$t('communication_pin_failed') || 'Could not update pin. Please try again.',
          type: 'error',
          duration: 3000,
        });
      } finally {
        this.pinningId = null;
      }
    },
    fallbackCopy(text) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        new Toast({
          message: this.$t('communication_copied') || 'Copied to clipboard.',
          type: 'success',
          duration: 2500,
        });
      } catch {
        void 0;
      }
      document.body.removeChild(ta);
    },
    getDraftKey() {
      return `communication_draft_${this.siteId || 'none'}`;
    },
    saveDraft() {
      if (!this.siteId) return;
      const draft = {
        content: this.formContent,
        type: this.formType,
        timestamp: Date.now(),
      };
      try {
        localStorage.setItem(this.getDraftKey(), JSON.stringify(draft));
      } catch {
        // ignore draft persistence errors (e.g. private mode / quota)
      }
    },
    loadDraft() {
      if (!this.siteId) return;
      try {
        const draftStr = localStorage.getItem(this.getDraftKey());
        if (draftStr) {
          const draft = JSON.parse(draftStr);
          // Only restore if draft is less than 24 hours old
          if (draft.timestamp && Date.now() - draft.timestamp < 24 * 60 * 60 * 1000) {
            this.formContent = draft.content || '';
            this.formType = draft.type || 'general';
          } else {
            this.clearDraft();
          }
        }
      } catch {
        // ignore corrupt or unreadable draft
      }
    },
    clearDraft() {
      try {
        localStorage.removeItem(this.getDraftKey());
      } catch {
        // ignore
      }
    },
    clearFilters() {
      this.searchQuery = '';
      this.typeFilter = '';
    },
  },
  components: {
    Loading,
    Modal,
    Select,
    Option,
  },
};
</script>

<style lang="scss" scoped src="./communication/Communication.scss"></style>
